import * as React from 'react';
import { ArrowUpRight, Check, ChevronRight } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  setupChecklistRecipe,
  setupStepMarkRecipe,
  setupStepRecipe,
} from '../recipes/mobile.recipe';
import { Icon } from './Icon';
import { Stack } from './Layout';
import { Text } from './Text';

export interface SetupChecklistProps
  extends React.HTMLAttributes<HTMLUListElement> {
  /** SetupSteps. */
  children: React.ReactNode;
}

/**
 * What is left before a new company can operate, as the Figma's setup
 * checklist draws it: one SetupStep a row, with a rule between each.
 *
 *   <SetupChecklist>
 *     <SetupStep title="Business Profile" description="Name and location"
 *       done={profileDone} onClick={openProfile} />
 *     <SetupStep title="Invite your team" description="Add your team to Relay"
 *       external onClick={openTeam} />
 *   </SetupChecklist>
 */
export const SetupChecklist = React.forwardRef<
  HTMLUListElement,
  SetupChecklistProps
>(function SetupChecklist({ className, children, ...props }, ref) {
  return (
    <ul ref={ref} className={cn(setupChecklistRecipe(), className)} {...props}>
      {React.Children.map(children, (child) =>
        child ? <li>{child}</li> : null
      )}
    </ul>
  );
});

export interface SetupStepProps {
  title: React.ReactNode;
  /** The muted line under the title: what the step is. */
  description?: React.ReactNode;
  /** Ticked: the ring fills and says so to a screen reader. */
  done?: boolean;
  /** Opens the step. */
  onClick: () => void;
  /**
   * The step is done outside the app, on the dashboard in the phone's
   * browser: an arrow out instead of the caret, as SettingsRow marks it.
   */
  external?: boolean;
  disabled?: boolean;
}

/**
 * One step of a SetupChecklist: the ring, then the name over its line,
 * and the caret. The whole row is one button.
 */
export function SetupStep({
  title,
  description,
  done = false,
  onClick,
  external = false,
  disabled = false,
}: SetupStepProps) {
  return (
    <button
      type="button"
      className={setupStepRecipe()}
      onClick={onClick}
      disabled={disabled}
    >
      <Stack gap={2} grow>
        <span className={setupStepMarkRecipe({ done })}>
          {done ? <Icon icon={Check} label="Done" /> : null}
        </span>
        <Stack gap={1}>
          <Text as="span" variant="body-lg-strong">
            {title}
          </Text>
          {description ? (
            <Text as="span" variant="body" tone="muted">
              {description}
            </Text>
          ) : null}
        </Stack>
      </Stack>
      <Icon
        icon={external ? ArrowUpRight : ChevronRight}
        size="md"
        tone="muted"
      />
    </button>
  );
}
